// Money is integer cents everywhere; screens show dollars.
// The dining API answers UTC times without an offset ("2026-10-28T18:02:11"); they are
// shown on the restaurant's clock (setDiningZone).

export function money(cents) {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return '—';
  const n = Number(cents);
  const sign = n < 0 ? '−' : '';
  return `${sign}$${(Math.abs(n) / 100).toFixed(2)}`;
}

export function parseTime(iso) {
  if (!iso) return null;
  const s = String(iso);
  const withZone = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const t = Date.parse(withZone);
  return Number.isFinite(t) ? t : null;
}

// THE RESTAURANT'S CLOCK: every time a dining screen shows (a ticket's "Sent 7:12 PM",
// the header clock, today's date) is on the restaurant's own zone (GET /api/dining/me
// `timezone`, set once by the session) - never the device's. Ages / timers are
// differences of instants and do not depend on it.
let diningZone;

function validZone(tz) {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch (e) {
    return undefined;
  }
}

export function setDiningZone(tz) {
  diningZone = validZone(tz);
  return diningZone;
}

export function getDiningZone() {
  return diningZone;
}

function onClock(t, opts) {
  return new Date(t).toLocaleTimeString('en-US', { ...opts, timeZone: diningZone });
}

export function clock(iso) {
  const t = parseTime(iso);
  if (t === null) return '';
  return onClock(t, { hour: 'numeric', minute: '2-digit' });
}

export function clockSeconds(iso) {
  const t = parseTime(iso);
  if (t === null) return '';
  return onClock(t, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

export function nowClock(date = new Date()) {
  return onClock(date.getTime(), { hour: 'numeric', minute: '2-digit' });
}

export function todayLabel(date = new Date()) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: diningZone });
}

// today's calendar day on the restaurant's clock, "2026-10-29"
export function todayYmd(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: diningZone })
    .formatToParts(date);
  const get = (k) => parts.find((p) => p.type === k)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// "7:12" (m:ss) / "1:04:12" (h:mm:ss) since a moment.
export function elapsed(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function secondsSince(iso, now = Date.now()) {
  const t = parseTime(iso);
  return t === null ? 0 : Math.max(0, (now - t) / 1000);
}

export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

// "Minh Nguyen" -> "Minh N."
export function shortName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
}

export function plural(n, one, many) {
  return `${n} ${n === 1 ? one : (many || `${one}s`)}`;
}

// a share {num, den} or "1/2" -> "½"
const FRACTIONS = { '1/2': '½', '1/3': '⅓', '2/3': '⅔', '1/4': '¼', '3/4': '¾', '1/5': '⅕', '1/6': '⅙', '1/8': '⅛' };
export function shareLabel(share) {
  if (!share) return '';
  const text = typeof share === 'string' ? share
    : (share.num !== undefined ? `${share.num}/${share.den}` : `${share[0]}/${share[1]}`);
  if (text === '1/1' || text === '1') return '';
  return FRACTIONS[text] || text;
}

export function modifierText(item) {
  const mods = (item?.modifiers || []).map((m) => m.name).filter(Boolean);
  return mods.join(' · ');
}

export const ALLERGY_PREFIX = 'ALLERGY:';

// An allergy the waiter typed for the kitchen ("ALLERGY: peanut") - always shown red, never hidden.
export function splitNotes(notes) {
  const text = String(notes || '').trim();
  if (!text) return { allergy: null, note: null };
  const lines = text.split(/\n+/);
  const allergyLines = lines.filter((l) => /allerg/i.test(l));
  const others = lines.filter((l) => !/allerg/i.test(l));
  const allergy = allergyLines.map((l) => l.replace(/^\s*allerg(y|ies)\s*[:\-·]?\s*/i, '').trim()).filter(Boolean).join(', ');
  return { allergy: allergyLines.length ? (allergy || 'see note') : null, note: others.join(' · ') || null };
}

export function joinNotes(note, allergy) {
  const parts = [];
  if (allergy && allergy.trim()) parts.push(`${ALLERGY_PREFIX} ${allergy.trim()}`);
  if (note && note.trim()) parts.push(note.trim());
  return parts.join('\n') || null;
}

// ONE KITCHEN (docs/DINING_API_2026-10-28.md §11): a register / kiosk / online
// order's ticket has no table - it carries its channel, order number, the
// customer's name and the pickup time instead.
const CHANNEL_LABEL = { register: 'Register', kiosk: 'Kiosk', online: 'Online' };
const ORDER_TYPE_LABEL = { pickup: 'Takeout', takeout: 'Takeout', dine_in: 'For here', delivery: 'Delivery' };

export function isChannelTicket(t) {
  return !!t && !!t.channel && t.channel !== 'table';
}

export function orderTypeLabel(t) {
  return ORDER_TYPE_LABEL[t?.order_type] || 'Takeout';
}

// "T8" for a table; "#2041" / "K041" for a register / kiosk / online order
export function ticketPlace(t) {
  if (isChannelTicket(t)) return t.order_label || `#${t.order_id}`;
  return t?.table_number ? `T${t.table_number}` : `#${t?.order_id}`;
}

// "Takeout #2041" / "Table 8"
export function ticketTitle(t) {
  if (isChannelTicket(t)) return `${orderTypeLabel(t)} ${t.order_label || `#${t.order_id}`}`;
  return `Table ${t?.table_number ?? '—'}`;
}

// "Online · Linh T. · Pickup 8:10 PM"
export function channelLine(t) {
  if (!isChannelTicket(t)) return null;
  return [CHANNEL_LABEL[t.channel] || t.channel, shortName(t.customer_name), t.pickup_at ? `Pickup ${clock(t.pickup_at)}` : null]
    .filter(Boolean).join(' · ');
}
